/**
 * Survey Questions dialog used by the policy detail page's Regenerate
 * smart-gate. Renders the same `ReadinessQuestionnaireRow` the
 * onboarding wizard uses, scoped to the catalog blueprints applicable
 * to the policy. Auto-saves on selection (the row writes through to
 * `readinessQuestionnaire.submitAnswer` → `saveDraftAnswer`), so the
 * user can confirm/answer/override in place before clicking Regenerate.
 */
import { useCallback, useMemo } from 'react'
import { useParams } from '@tanstack/react-router'
import {
  useMutation,
  useQuery,
  useQueries,
  useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'
import { AlertTriangle, ChevronDown, Loader2, RefreshCw, ShieldCheck } from 'lucide-react'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@deepguide-ai/ui/collapsible'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@deepguide-ai/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { trpc } from '@/lib/trpc'
import {
  ReadinessQuestionnaireRow,
  type AttestationAnswerValue,
  type DimensionStateMap,
  type QuestionnaireBlueprintForRow,
  type RowInitialState,
  type RowState,
} from '../onboarding/questionnaire/ReadinessQuestionnaireRow'

interface PolicyQuestionnaireDialogProps {
  programId: string
  policySlug: string
  policyTitle: string
  open: boolean
  onOpenChange: (next: boolean) => void
  onRegenerate?: () => void
  isRegenerating?: boolean
  readOnly?: boolean
  /** Match the smart-gate hint copy on the trigger side. */
  reviewNeeded?: boolean
}

export function PolicyQuestionnaireDialog({
  programId,
  policySlug,
  policyTitle,
  open,
  onOpenChange,
  onRegenerate,
  isRegenerating = false,
  readOnly = false,
  reviewNeeded = false,
}: PolicyQuestionnaireDialogProps) {
  const { workspaceId } = useParams({ strict: false }) as { workspaceId?: string }
  const queryClient = useQueryClient()
  // The rows auto-save *drafts*; the v2 generator reads committed answer
  // revisions (drafts supply only staleness timestamps). Without committing
  // first, "answer here, then regenerate" regenerates from the previous
  // answers — the exact promise this dialog makes.
  const commitMutation = useMutation(
    trpc.readinessQuestionnaire.commitSection.mutationOptions(),
  )
  const { mutateAsync: commitSection } = commitMutation as unknown as {
    mutateAsync: (v: unknown) => Promise<unknown>
  }

  // One commit path for both exits (Regenerate and plain close) so answers
  // are never left as drafts the generator can't see.
  const commitAnswers = useCallback(
    async (blueprintIds: string[]) => {
      if (blueprintIds.length === 0) return
      await commitSection({ programId, blueprintIds })
    },
    [commitSection, programId],
  )

  const sectionsQuery = useQuery({
    ...trpc.complianceProgram.getPolicyQuestionnaireBlueprints.queryOptions({
      programId,
      policySlug,
      // Keep core commitments reviewable alongside optional Extended choices.
      askedOnly: false,
    }),
    enabled: open,
  })

  const sections = useMemo(
    () => sectionsQuery.data?.sections ?? [],
    [sectionsQuery.data?.sections],
  )
  const allBlueprints = useMemo(
    () =>
      sections.flatMap((s) =>
        s.blueprints.map((bp) => ({
          ...bp,
          // ReadinessQuestionnaireRow expects a strict shape; the
          // server payload already matches `getSections`.
        })),
      ),
    [sections],
  )
  const questionGroups = [
    {
      key: 'core',
      title: 'Core questions',
      description: 'Practices and operational details relevant to this policy. Answer these first. If a practice needs work, record the gap rather than claiming it is in place.',
      inclusions: ['core', 'scope', 'context'],
    },
    {
      key: 'extended',
      title: 'Extended questions',
      description: 'Not required for your current program. Excluded from the policy by default. Include only practices your company chooses to commit to. Previously included practices stay included until you change the answer.',
      inclusions: ['extended'],
    },
  ].map((group) => ({
    ...group,
    sections: sections.map((section) => ({
      ...section,
      blueprints: section.blueprints.filter((bp) =>
        group.inclusions.includes(bp.policyScope?.inclusion ?? 'core'),
      ),
    })).filter((section) => section.blueprints.length > 0),
  })).filter((group) => group.sections.length > 0)

  // Hydrate per-row pre-fill state — same pattern QuestionnaireSectionScreen
  // uses for the onboarding wizard.
  const { data: rowStateData, anyRowLoading } = useQueries({
    queries: allBlueprints.map((bp) => ({
      ...trpc.readinessQuestionnaire.getRowState.queryOptions({
        programId,
        blueprintId: bp.id,
      }),
      enabled: open,
    })),
    combine: (
      results: Array<{
        data: unknown
        isLoading: boolean
        isPending: boolean
      }>
    ) => ({
      data: results.map((result) => result.data),
      anyRowLoading: results.some(
        (result) => result.isLoading || result.isPending
      ),
    }),
  })

  const rowStateById = useMemo(() => {
    const map = new Map<string, RowInitialState>()
    allBlueprints.forEach((bp, idx) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = rowStateData[idx] as any
      if (!data) return
      let sourceLabel: string | undefined
      if (data.answerSource === 'SCANNER' && data.scannerEvidence) {
        const providers = [
          ...new Set(
            data.scannerEvidence.runs.map(
              (run: { provider: string }) => run.provider
            )
          ),
        ]
        sourceLabel = `Verified by ${providers.join(', ')} scan`
      } else if (data.state === 'attested') {
        sourceLabel = 'Attested'
      }
      map.set(bp.id, {
        state: (data.state as RowState) ?? 'cold',
        attestationAnswer:
          (data.attestationAnswer as AttestationAnswerValue | null) ?? null,
        optionKey: data.optionKey ?? null,
        rationale: data.rationale ?? null,
        dimensionStates:
          (data.dimensionStates as DimensionStateMap | null) ?? null,
        importMeta: data.importMeta ?? null,
        sourceLabel,
        scannerEvidence: data.scannerEvidence ?? null,
        conflict: data.conflict ?? null,
        vendorHint: data.vendorHint ?? null,
      })
    })
    return map
  }, [allBlueprints, rowStateData])

  const isLoading =
    sectionsQuery.isLoading ||
    (open && allBlueprints.length > 0 && anyRowLoading)

  const handleAnswered = () => {
    // The row already writes through to the canonical fan-out helper.
    // Re-read the policy-side answers feed so the trigger's
    // `reviewNeeded` recomputes after the user picks an option.
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getPolicyAnswers.queryKey({
        programId,
        policySlug,
      }),
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Closing without regenerating still commits: the rows have already
        // written drafts, and a draft is invisible to the generator — leaving
        // it uncommitted silently discards what the user just answered.
        if (!next && !readOnly && allBlueprints.length > 0) {
          void commitAnswers(allBlueprints.map((bp) => bp.id)).catch(() => {
            toast.error('Could not save your answers. Please try again.')
          })
        }
        onOpenChange(next)
      }}
    >
      <DialogContent className='flex max-h-[85vh] !max-w-2xl flex-col overflow-hidden gap-0 p-0'>
        <DialogHeader>
          <DialogTitle className='text-[14px] font-semibold text-text-secondary'>
            Review requirements: {policyTitle}
          </DialogTitle>
          <p className='mt-1 text-[12px] text-text-subtle'>
            Review what your policy commits to. Report a gap, describe a different safeguard,
            or request an exception where needed. Answers save automatically; use the
            Save buttons for plans and requests. Regenerate applies your changes to the policy.
          </p>
        </DialogHeader>

        <div className='flex min-h-0 flex-1 flex-col'>
          {isLoading ? (
            <div className='flex items-center gap-2 px-6 py-8 text-[12px] text-text-placeholder'>
              <Loader2 className='h-3.5 w-3.5 animate-spin' />
              Loading questions…
            </div>
          ) : sections.length === 0 ? (
            <p className='px-6 py-8 text-center text-[12px] text-text-placeholder'>
              No questions apply to this policy.
            </p>
          ) : (
            <Tabs defaultValue={questionGroups[0]?.key} className='min-h-0 flex-1 gap-0'>
              <TabsList aria-label='Question groups' className='bg-card w-full shrink-0 px-6'>
                {questionGroups.map((group) => (
                  <TabsTrigger key={group.key} value={group.key} className='gap-2'>
                    {group.key === 'core' ? 'Core' : group.key === 'extended' ? 'Extended' : group.title}
                    {group.key === 'extended' && <Badge variant='outline'>Optional</Badge>}
                    <span className='text-text-subtle text-xs tabular-nums'>
                      {group.sections.reduce((count, section) => count + section.blueprints.length, 0)}
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
              <div className='min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-4'>
                {(sectionsQuery.data?.remediationFindings?.length ?? 0) > 0 && (
                  <section aria-label='Tracked coverage gaps' className='border-status-orange/40 bg-status-orange-xsubtle rounded-md border'>
                    <Collapsible>
                      <CollapsibleTrigger asChild>
                        <Button variant='ghost' className='group h-auto w-full justify-start gap-3 whitespace-normal p-4 text-left hover:bg-status-orange-bg'>
                          <AlertTriangle className='text-status-orange size-4 shrink-0' />
                          <span className='min-w-0 flex-1'>
                            <span className='text-foreground block text-sm font-semibold'>
                              Tracked coverage gaps ({sectionsQuery.data?.remediationFindings?.length})
                            </span>
                            <span className='text-text-subtle mt-1 block text-xs font-normal'>
                              Remediation is tracked separately from your answers. View owners, dates, and evidence needed to close each gap.
                            </span>
                          </span>
                          <ChevronDown className='text-status-orange size-4 shrink-0 transition-transform group-data-[state=open]:rotate-180' />
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <ul className='space-y-3 px-4 pb-4'>
                          {sectionsQuery.data?.remediationFindings?.map((finding) => (
                            <li key={finding.id} className='border-border border-t pt-3 text-xs'>
                              <p className='text-foreground font-medium'>{finding.title}</p>
                              <p className='text-text-subtle mt-1'>
                                {finding.ownerName ?? 'Owner needed'} · {finding.dueDate ? `Due ${new Date(finding.dueDate).toLocaleDateString()}` : 'Target date needed'}
                              </p>
                              {(finding.taskId || finding.testId) && workspaceId && (
                                <a href={finding.taskId ? `/workspaces/${workspaceId}/tasks/${finding.taskId}` : `/workspaces/${workspaceId}/controls?test=${finding.testId}`} className='text-primary mt-1 inline-block underline underline-offset-2'>
                                  {finding.taskId ? 'View remediation task' : `View ${finding.testCode ?? 'test'} and remediation`}
                                </a>
                              )}
                            </li>
                          ))}
                        </ul>
                      </CollapsibleContent>
                    </Collapsible>
                  </section>
                )}
                {questionGroups.map((group) => (
                  <TabsContent key={group.key} value={group.key} forceMount className='data-[state=inactive]:hidden'>
                    <section aria-label={group.title}>
                      <div className={`mb-5 rounded-md border p-4 ${group.key === 'core' ? 'border-status-blue/40 bg-status-blue-xsubtle' : 'border-border bg-surface-subtle'}`}>
                        <div className='flex flex-wrap items-center gap-2'>
                          {group.key === 'core' && <ShieldCheck className='text-status-blue size-4' />}
                          <h2 className='text-foreground text-base font-semibold'>{group.title}</h2>
                          {group.key === 'extended' && <Badge variant='outline'>Optional · Excluded by default</Badge>}
                        </div>
                        <p className='text-text-subtle mt-1 text-xs leading-relaxed'>{group.description}</p>
                      </div>
                      <div className='space-y-6'>
                        {group.sections.map((section) => (
                          <section key={section.sectionKey}>
                            <h3 className='mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-subtle'>
                              {section.sectionTitle}
                            </h3>
                            <div className='flex flex-col gap-3'>
                              {section.blueprints.map((bp) => (
                                <ReadinessQuestionnaireRow
                                  key={bp.id}
                                  programId={programId}
                                  blueprint={bp as QuestionnaireBlueprintForRow}
                                  initial={
                                    rowStateById.get(bp.id) ?? {
                                      state: 'cold',
                                      attestationAnswer: null,
                                      rationale: null,
                                    }
                                  }
                                  onAnswered={handleAnswered}
                                />
                              ))}
                            </div>
                          </section>
                        ))}
                      </div>
                    </section>
                  </TabsContent>
                ))}
              </div>
            </Tabs>
          )}
        </div>

        {!readOnly && onRegenerate && (
          <div className='flex items-center justify-between gap-2 border-t border-border bg-surface-subtle px-6 py-3'>
            <p className='text-[11px] text-text-subtle'>
              {reviewNeeded
                ? 'Complete the missing answers above before regenerating.'
                : 'Regenerate to update the policy with these requirements and saved details.'}
            </p>
            <div className='flex items-center gap-2'>
              <button
                type='button'
                onClick={() => {
                  if (!readOnly && allBlueprints.length > 0) {
                    void commitAnswers(allBlueprints.map((bp) => bp.id)).catch(
                      () => {
                        toast.error(
                          'Could not save your answers. Please try again.',
                        )
                      },
                    )
                  }
                  onOpenChange(false)
                }}
                className='inline-flex h-7 items-center rounded-sm border border-border-divider bg-card px-3 text-[12px] font-medium text-foreground hover:bg-surface-subtle'
              >
                Cancel
              </button>
              <button
                type='button'
                disabled={isRegenerating || commitMutation.isPending}
                onClick={() => {
                  void (async () => {
                    try {
                      await commitAnswers(allBlueprints.map((bp) => bp.id))
                    } catch (err) {
                      toast.error(
                        err instanceof Error
                          ? err.message
                          : 'Could not save your answers. The draft would not reflect them.',
                      )
                      return
                    }
                    onOpenChange(false)
                    onRegenerate()
                  })()
                }}
                className='inline-flex h-7 items-center gap-1.5 rounded-sm bg-primary px-3 text-[12px] font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50'
              >
                {isRegenerating || commitMutation.isPending ? (
                  <Loader2 className='h-3 w-3 animate-spin' />
                ) : (
                  <RefreshCw className='h-3 w-3' />
                )}
                Regenerate
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
